/**
 * Pictures of the sample project, generated at runtime (W2-14 onboarding-wizard; DESIGN.md §5.14 "no third-party
 * media ships"): the illustration, the image line's texture and the GIF line's animation. Pure and deterministic
 * (same output every time), so the sample's assets are content-addressed to the same ids on every run.
 *
 *  - `encodePng`: 8-bit RGBA PNG, one filter byte (0) per row, zlib through fflate;
 *  - `encodeGif`: GIF89a with a global colour table, the NETSCAPE2.0 loop extension, a Graphic Control Extension per
 *    frame (delay in 1/100 s, which Phira reads x 10 ms without clamping) and variable-width LZW image data.
 */

import { zlibSync } from 'fflate';

// ---------------------------------------------------------------------------------------------------------------
// PNG
// ---------------------------------------------------------------------------------------------------------------

const CRC_TABLE: Uint32Array = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = (c & 1) !== 0 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  return t;
})();

function crc32(bytes: Uint8Array): number {
  let c = 0xffffffff;
  for (const b of bytes) c = (CRC_TABLE[(c ^ b) & 0xff] ?? 0) ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

function u32be(v: number): Uint8Array {
  return new Uint8Array([(v >>> 24) & 0xff, (v >>> 16) & 0xff, (v >>> 8) & 0xff, v & 0xff]);
}

function concat(parts: readonly Uint8Array[]): Uint8Array {
  const total = parts.reduce((n, p) => n + p.length, 0);
  const out = new Uint8Array(total);
  let o = 0;
  for (const p of parts) {
    out.set(p, o);
    o += p.length;
  }
  return out;
}

/** Bytes of an ASCII string. */
function asciiBytes(s: string): number[] {
  const out: number[] = [];
  for (let i = 0; i < s.length; i++) out.push(s.charCodeAt(i) & 0x7f);
  return out;
}

function pngChunk(type: string, data: Uint8Array): Uint8Array {
  const typed = concat([new Uint8Array(asciiBytes(type)), data]);
  return concat([u32be(data.length), typed, u32be(crc32(typed))]);
}

/** 8-bit RGBA PNG of `rgba` (w x h x 4 bytes, straight alpha). */
export function encodePng(width: number, height: number, rgba: Uint8Array): Uint8Array {
  if (rgba.length !== width * height * 4) throw new RangeError('encodePng: pixel buffer size mismatch');
  const stride = width * 4;
  const raw = new Uint8Array((stride + 1) * height);
  for (let y = 0; y < height; y++) {
    raw[y * (stride + 1)] = 0;
    raw.set(rgba.subarray(y * stride, (y + 1) * stride), y * (stride + 1) + 1);
  }
  const ihdr = concat([u32be(width), u32be(height), new Uint8Array([8, 6, 0, 0, 0])]);
  return concat([
    new Uint8Array([0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]),
    pngChunk('IHDR', ihdr),
    pngChunk('IDAT', zlibSync(raw, { level: 6 })),
    pngChunk('IEND', new Uint8Array(0)),
  ]);
}

// ---------------------------------------------------------------------------------------------------------------
// GIF
// ---------------------------------------------------------------------------------------------------------------

export interface GifFrame {
  /** Palette index per pixel (w x h). */
  readonly indices: Uint8Array;
  /** Delay in 1/100 s (Phira: x 10 ms). */
  readonly delayCs: number;
}

/** Variable-width LZW codes packed LSB first (GIF image data, before splitting into sub-blocks). */
export function lzwEncode(indices: Uint8Array, minCodeSize: number): Uint8Array {
  const out: number[] = [];
  let acc = 0;
  let bits = 0;
  let size = minCodeSize + 1;
  const emit = (code: number): void => {
    acc |= code << bits;
    bits += size;
    while (bits >= 8) {
      out.push(acc & 0xff);
      acc >>>= 8;
      bits -= 8;
    }
  };
  const clear = 1 << minCodeSize;
  const eoi = clear + 1;
  let next = eoi + 1;
  let table = new Map<number, number>();
  emit(clear);
  if (indices.length === 0) {
    emit(eoi);
    if (bits > 0) out.push(acc & 0xff);
    return new Uint8Array(out);
  }
  let prefix = indices[0] ?? 0;
  for (let i = 1; i < indices.length; i++) {
    const k = indices[i] ?? 0;
    const key = prefix * 256 + k;
    const hit = table.get(key);
    if (hit !== undefined) {
      prefix = hit;
      continue;
    }
    emit(prefix);
    if (next === 4096) {
      emit(clear);
      table = new Map();
      next = eoi + 1;
      size = minCodeSize + 1;
    } else {
      // The decoder widens its codes when the code it is about to add needs one more bit.
      if (next >= 1 << size) size++;
      table.set(key, next++);
    }
    prefix = k;
  }
  emit(prefix);
  emit(eoi);
  if (bits > 0) out.push(acc & 0xff);
  return new Uint8Array(out);
}

function u16le(v: number): number[] {
  return [v & 0xff, (v >>> 8) & 0xff];
}

/** Splits data into GIF sub-blocks (<= 255 bytes each) followed by the block terminator. */
function subBlocks(data: Uint8Array): number[] {
  const out: number[] = [];
  for (let i = 0; i < data.length; i += 255) {
    const chunk = data.subarray(i, Math.min(data.length, i + 255));
    out.push(chunk.length, ...chunk);
  }
  out.push(0);
  return out;
}

/**
 * Animated GIF89a looping forever. `palette` holds up to 256 RGB colours; index `transparent` (when given) is
 * transparent in every frame. Frames are full-size and replace each other (disposal "restore to background").
 */
export function encodeGif(
  width: number,
  height: number,
  palette: ReadonlyArray<readonly [number, number, number]>,
  frames: readonly GifFrame[],
  transparent: number | null = null,
): Uint8Array {
  if (palette.length === 0 || palette.length > 256) throw new RangeError('encodeGif: 1-256 palette colours');
  let bitsPerIndex = 1;
  while (1 << bitsPerIndex < palette.length) bitsPerIndex++;
  const tableSize = 1 << bitsPerIndex;
  const bytes: number[] = asciiBytes('GIF89a');
  bytes.push(...u16le(width), ...u16le(height), 0x80 | ((bitsPerIndex - 1) << 4) | (bitsPerIndex - 1), 0, 0);
  for (let i = 0; i < tableSize; i++) {
    const c = palette[i] ?? [0, 0, 0];
    bytes.push(c[0] & 0xff, c[1] & 0xff, c[2] & 0xff);
  }
  // NETSCAPE2.0: loop forever.
  bytes.push(0x21, 0xff, 0x0b, ...asciiBytes('NETSCAPE2.0'), 0x03, 0x01, 0x00, 0x00, 0x00);
  const minCode = Math.max(2, bitsPerIndex);
  for (const f of frames) {
    if (f.indices.length !== width * height) throw new RangeError('encodeGif: frame size mismatch');
    const disposal = 2 << 2;
    bytes.push(
      0x21,
      0xf9,
      0x04,
      disposal | (transparent === null ? 0 : 1),
      ...u16le(Math.max(0, Math.round(f.delayCs))),
    );
    bytes.push(transparent ?? 0, 0x00);
    bytes.push(0x2c, ...u16le(0), ...u16le(0), ...u16le(width), ...u16le(height), 0x00);
    bytes.push(minCode, ...subBlocks(lzwEncode(f.indices, minCode)));
  }
  bytes.push(0x3b);
  return new Uint8Array(bytes);
}

// ---------------------------------------------------------------------------------------------------------------
// The sample's pictures
// ---------------------------------------------------------------------------------------------------------------

type Rgb = readonly [number, number, number];

function mix(a: Rgb, b: Rgb, t: number): Rgb {
  const u = Math.max(0, Math.min(1, t));
  return [a[0] + (b[0] - a[0]) * u, a[1] + (b[1] - a[1]) * u, a[2] + (b[2] - a[2]) * u];
}

/** Colours of the sample's artwork (asset content, not UI colours). */
const ART = {
  deep: [18, 24, 52] as Rgb,
  dusk: [92, 46, 120] as Rgb,
  glow: [255, 196, 120] as Rgb,
  sky: [80, 196, 245] as Rgb,
  white: [245, 245, 250] as Rgb,
} as const;

/** Illustration (16:9): a dusk gradient with concentric rings and a horizon line. */
export function sampleIllustration(
  width = 1280,
  height = 720,
): { readonly bytes: Uint8Array; readonly width: number; readonly height: number } {
  const px = new Uint8Array(width * height * 4);
  const cx = width * 0.62;
  const cy = height * 0.58;
  const horizon = height * 0.72;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      let c = mix(ART.deep, ART.dusk, y / height + (x / width) * 0.25);
      const d = Math.hypot(x - cx, y - cy);
      const glow = Math.max(0, 1 - d / (height * 0.55));
      c = mix(c, ART.glow, glow * glow * 0.85);
      const ring = Math.abs(((d / (height * 0.09)) % 1) - 0.5);
      if (ring < 0.04 && d < height * 0.5) c = mix(c, ART.white, 0.35 * (1 - d / (height * 0.5)));
      if (Math.abs(y - horizon) < 2.5) c = mix(c, ART.sky, 0.9);
      const o = (y * width + x) * 4;
      px[o] = Math.round(c[0]);
      px[o + 1] = Math.round(c[1]);
      px[o + 2] = Math.round(c[2]);
      px[o + 3] = 255;
    }
  }
  return { bytes: encodePng(width, height, px), width, height };
}

/** Image line texture: a soft ring with a star, transparent outside (square). */
export function sampleTexture(size = 256): {
  readonly bytes: Uint8Array;
  readonly width: number;
  readonly height: number;
} {
  const px = new Uint8Array(size * size * 4);
  const r0 = size * 0.42;
  const c = size / 2;
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const dx = x + 0.5 - c;
      const dy = y + 0.5 - c;
      const d = Math.hypot(dx, dy);
      const a = Math.atan2(dy, dx);
      const star = r0 * (0.55 + 0.25 * Math.cos(a * 5));
      const ring = Math.max(0, 1 - Math.abs(d - r0) / (size * 0.035));
      const inStar = d < star ? 1 : Math.max(0, 1 - (d - star) / 2);
      const alpha = Math.max(ring, inStar);
      const col = d < star ? mix(ART.glow, ART.white, 1 - d / star) : ART.sky;
      const o = (y * size + x) * 4;
      px[o] = Math.round(col[0]);
      px[o + 1] = Math.round(col[1]);
      px[o + 2] = Math.round(col[2]);
      px[o + 3] = Math.round(alpha * 255);
    }
  }
  return { bytes: encodePng(size, size, px), width: size, height: size };
}

/** Frames and delay of the GIF line's animation. */
export const SAMPLE_GIF_FRAMES = 8;
export const SAMPLE_GIF_DELAY_CS = 8;

/** GIF line texture: eight dots chasing round a circle (transparent background, loops forever). */
export function sampleGif(size = 128): { readonly bytes: Uint8Array; readonly width: number; readonly height: number } {
  const palette: Array<readonly [number, number, number]> = [[0, 0, 0], ART.white, ART.sky, ART.glow];
  const frames: GifFrame[] = [];
  const c = size / 2;
  const orbit = size * 0.34;
  const dot = size * 0.09;
  for (let f = 0; f < SAMPLE_GIF_FRAMES; f++) {
    const idx = new Uint8Array(size * size);
    for (let i = 0; i < SAMPLE_GIF_FRAMES; i++) {
      const a = (i / SAMPLE_GIF_FRAMES) * Math.PI * 2 - Math.PI / 2;
      const px = c + Math.cos(a) * orbit;
      const py = c + Math.sin(a) * orbit;
      const lead = (i - f + SAMPLE_GIF_FRAMES) % SAMPLE_GIF_FRAMES;
      const colour = lead === 0 ? 3 : lead <= 2 ? 2 : 1;
      const r = lead === 0 ? dot * 1.25 : dot * (1 - lead * 0.06);
      for (let y = Math.floor(py - r); y <= Math.ceil(py + r); y++) {
        for (let x = Math.floor(px - r); x <= Math.ceil(px + r); x++) {
          if (x < 0 || y < 0 || x >= size || y >= size) continue;
          if (Math.hypot(x + 0.5 - px, y + 0.5 - py) <= r) idx[y * size + x] = colour;
        }
      }
    }
    frames.push({ indices: idx, delayCs: SAMPLE_GIF_DELAY_CS });
  }
  return { bytes: encodeGif(size, size, palette, frames, 0), width: size, height: size };
}
